import { useEffect, useState } from 'react';
import { api, type CallieCosts as Costs, type CostRow } from '../lib/api';

/**
 * What Callie costs (D91) — CallCovered's page, not a client's.
 *
 * This is the cost of running the platform, so the route behind it is owner-only and this tab is
 * not offered to a client's schedulers. It answers three questions and nothing else: what the
 * bill is, who is spending it, and which kind of question is dear. "Kind" is the tools a turn
 * used, never what anybody typed — reviewing a bill is no reason to read a physician's messages.
 */

const SPANS = [7, 14, 30, 90];

const money = (n: number) => `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`;
const count = (n: number) => n.toLocaleString();

function Rows({ title, rows, what }: { title: string; rows: CostRow[]; what: string }) {
  if (!rows.length) return null;
  return (
    <section className="costgroup">
      <h3>{title}</h3>
      <table className="grid">
        <thead>
          <tr>
            <th scope="col">{what}</th>
            <th scope="col" className="num">Turns</th>
            <th scope="col" className="num">Rounds each</th>
            <th scope="col" className="num">Cost</th>
            <th scope="col" className="num">Per turn</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.key}</th>
              <td className="num">{count(r.turns)}</td>
              <td className="num">{(r.rounds / r.turns).toFixed(1)}</td>
              <td className="num">{money(r.usd)}</td>
              <td className="num">{money(r.usd / r.turns)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function CallieCosts() {
  const [days, setDays] = useState(7);
  const [costs, setCosts] = useState<Costs | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setCosts(null);
    setProblem(null);
    api
      .callieCosts(days)
      .then((c) => live && setCosts(c))
      .catch((e: unknown) => live && setProblem(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [days]);

  return (
    <section className="costs">
      <h2>Callie costs</h2>
      <p className="muted small">
        What Callie has cost to run, from the token counts recorded with each of her replies. CallCovered only — a
        client's schedulers do not see this tab. A summary also goes out on Mondays once an address is set.
      </p>

      <div className="gridtools noprint">
        {SPANS.map((d) => (
          <button key={d} type="button" className={d === days ? 'chip on' : 'chip'} onClick={() => setDays(d)} aria-pressed={d === days}>
            Last {d} days
          </button>
        ))}
      </div>

      {problem && <p className="error">{problem}</p>}
      {!costs && !problem && <p className="muted small">Working…</p>}

      {costs && costs.turns === 0 && (
        <p className="muted">
          No priced turns since {costs.since}. Replies answered before the counts existed carry none, and the stand-in
          used locally never does.
        </p>
      )}

      {costs && costs.turns > 0 && (
        <>
          <div className="costtotals">
            <p className="bignum">{money(costs.usd)}</p>
            <p className="muted small">
              {count(costs.turns)} turn{costs.turns === 1 ? '' : 's'} since {costs.since} · {money(costs.perTurn)} a turn
            </p>
          </div>

          <table className="grid costtokens">
            <tbody>
              <tr>
                <th scope="row">Fresh input</th>
                <td className="num">{count(costs.tokens.input)}</td>
                <td className="muted small">never cached — the question, and each tool's answer</td>
              </tr>
              <tr>
                <th scope="row">Cache writes</th>
                <td className="num">{count(costs.tokens.cacheWrite)}</td>
                <td className="muted small">storing the prompt; twice the base price, then free to re-read for an hour</td>
              </tr>
              <tr>
                <th scope="row">Cache reads</th>
                <td className="num">{count(costs.tokens.cacheRead)}</td>
                <td className="muted small">a tenth of the base price — where most of the input should be</td>
              </tr>
              <tr>
                <th scope="row">Output</th>
                <td className="num">{count(costs.tokens.output)}</td>
                <td className="muted small">what Callie wrote; five times the price of input</td>
              </tr>
            </tbody>
          </table>

          {/* The one number that says whether the office is sharing one cached prompt. If it falls,
              something has started varying per person or per day again and every login is paying
              to re-store the prompt. Worth saying out loud rather than leaving to be noticed. */}
          <p className={costs.cacheRatio !== null && costs.cacheRatio < 5 ? 'error' : 'muted small'}>
            {costs.cacheRatio === null
              ? 'Every request read the cache; nothing was re-stored this period.'
              : `${costs.cacheRatio.toFixed(1)} cache reads per token written.${
                  costs.cacheRatio < 5
                    ? ' That is low — the prompt is being re-stored too often, which usually means something in it started varying per person or per day again.'
                    : ' Healthy: the office is sharing one stored prompt.'
                }`}
          </p>

          {costs.unpriced.length > 0 && (
            <p className="error small">
              Counted as $0 because there is no price for them: {costs.unpriced.join(', ')}. Add the rate in{' '}
              <code>cost.service.ts</code>, or this total is too low.
            </p>
          )}

          <Rows title="By person" rows={costs.byPerson} what="Who" />
          <Rows title="By kind of question" rows={costs.byKind} what="Tools it used" />
          <Rows title="By day" rows={costs.byDay} what="Day" />

          <section className="costgroup">
            <h3>The five dearest single turns</h3>
            <table className="grid">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col" className="num">Rounds</th>
                  <th scope="col">Tools it used</th>
                  <th scope="col" className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {costs.dearest.map((d, i) => (
                  <tr key={i}>
                    <th scope="row">{d.at}</th>
                    <td className="num">{d.rounds}</td>
                    <td>{d.kind}</td>
                    <td className="num">{money(d.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted small">
              A turn is one question and Callie's answer to it. Rounds are the times she went back to the model within
              that one answer — usually once to look something up and once to reply. Each round re-reads the whole
              conversation, so rounds are what multiply a turn's cost.
            </p>
          </section>
        </>
      )}
    </section>
  );
}
